import { useCallback, useMemo } from 'react';
import { MomentInput } from 'moment';
import { upperFirst, get } from 'lodash';

import { RouteItemStateType } from '~globals/enums';
import { OrderBase } from '~services/order/types';
import { formatterDate, formatterEstimatedDateTime } from '~utils/formatter';
import { isDelayedOrder } from '~utils/order';

interface OrderStateTextItem {
  getName: () => string;
  getLabel: () => string;
  getDescription: () => string;
}

type OrderStateText = Record<RouteItemStateType, OrderStateTextItem>;

type OrderStateTextHookParams = Pick<
  OrderBase,
  | 'routeItemStateTypeId'
  | 'routeItemId'
  | 'contactName'
  | 'estimatedArrivalDateTime'
  | 'realArrivalDateTime'
>;

const getParseObjectDate = (
  date: MomentInput,
): { currentDay: string; date: string; time: string } => ({
  currentDay: upperFirst(formatterDate(date, { format: 'dddd' })),
  date: formatterDate(date, { format: 'DD/MM/YYYY' }),
  time: formatterDate(date, { format: 'hh:mm A' }),
});

export const useOrderStateText = ({
  routeItemStateTypeId,
  routeItemId,
  contactName,
  estimatedArrivalDateTime,
  realArrivalDateTime,
}: OrderStateTextHookParams): OrderStateTextItem => {
  const getLabelWithContactName = useCallback(
    (label: string) => {
      if (contactName) return `${contactName}, ${label}`;

      return upperFirst(label);
    },
    [contactName],
  );

  const isDelayed = useCallback(
    () => isDelayedOrder(estimatedArrivalDateTime),
    [estimatedArrivalDateTime],
  );

  const hasOrderWithoutRoute = useCallback(
    () => routeItemId === null,
    [routeItemId],
  );

  const orderStateTextList = useMemo<OrderStateText>(
    () => ({
      [RouteItemStateType.New]: {
        getName: () => 'Orden en preparación',
        getLabel: () => 'Hemos recibido tu orden',
        getDescription: () =>
          'Tu orden ingresó al sistema y está en proceso de gestión',
      },
      [RouteItemStateType.OnAgenda]: {
        getName: () => 'Orden en preparación',
        getLabel: () => 'Hemos recibido tu orden',
        getDescription: () =>
          'Tu orden ingresó al sistema y está en proceso de gestión',
      },
      [RouteItemStateType.InProgress]: {
        getName: () => (isDelayed() ? 'Orden demorada' : 'Orden en camino'),
        getLabel: () =>
          getLabelWithContactName(
            isDelayed() ? 'tu orden está demorada' : 'tu orden está en camino!',
          ),
        getDescription: () => {
          if (hasOrderWithoutRoute()) {
            return 'Estimamos que te estaremos visitando en el transcurso del día en tu domicilio.';
          }

          const { from, to } = formatterEstimatedDateTime(
            estimatedArrivalDateTime,
          );

          if (isDelayed()) {
            // eslint-disable-next-line max-len
            return `Hemos detectado una demora en la visita a tu domicilio (${from.time} - ${to.time}). Para más información comunícate con tu asesor.`;
          }

          // eslint-disable-next-line max-len
          return `Estimamos que te estaremos visitando entre las ${from.time} y ${to.time} en tu domicilio.`;
        },
      },
      [RouteItemStateType.FinalizedSuccess]: {
        getName: () => 'Orden completada',
        getLabel: () => getLabelWithContactName('tu visita ha sido realizada!'),
        getDescription: () => {
          if (hasOrderWithoutRoute()) {
            return 'Hemos visitado tu domicilio por tu orden y ha sido completada.';
          }

          const { currentDay, date, time } =
            getParseObjectDate(realArrivalDateTime);

          // eslint-disable-next-line max-len
          return `Hemos visitado tu domicilio el ${currentDay} ${date} a las ${time}.`;
        },
      },
      [RouteItemStateType.FinalizedError]: {
        getName: () => 'Orden no completada',
        getLabel: () =>
          getLabelWithContactName('hubo un inconveniente con tu visita'),
        getDescription: () => {
          if (hasOrderWithoutRoute()) {
            // eslint-disable-next-line max-len
            return 'No hemos podido completar la orden ya que tuvimos inconvenientes en la visita. Para más información comunícate con tu asesor.';
          }

          const { currentDay, date, time } =
            getParseObjectDate(realArrivalDateTime);

          // eslint-disable-next-line max-len
          return `Hemos visitado tu domicilio el ${currentDay} ${date} a las ${time}.`;
        },
      },
      [RouteItemStateType.WithoutVisiting]: {
        getName: () => 'Domicilio sin visitar',
        getLabel: () =>
          getLabelWithContactName('no hemos podido gestionar la orden'),
        getDescription: () => {
          if (hasOrderWithoutRoute()) {
            // eslint-disable-next-line max-len
            return 'Tu orden fue marcada como sin visitar. Para más información comunícate con tu asesor.';
          }

          const { currentDay, date, time } =
            getParseObjectDate(realArrivalDateTime);

          // eslint-disable-next-line max-len
          return `Tu orden fue marcada como sin visitar el ${currentDay} ${date} a las ${time}.`;
        },
      },
    }),
    [
      getLabelWithContactName,
      estimatedArrivalDateTime,
      realArrivalDateTime,
      isDelayed,
      hasOrderWithoutRoute,
    ],
  );

  const getCurrentOrderStateText = useMemo<OrderStateTextItem>(
    () => get(orderStateTextList, routeItemStateTypeId),
    [orderStateTextList, routeItemStateTypeId],
  );

  return getCurrentOrderStateText;
};
